import { config } from '../../utils/utils';
import store from '../../store';
import {
  AgentsStatsResponse,
  CompanyStatsResponse,
  TotalsStatsResponse,
  AdsPerformanceResponse,
} from './types';

/**
 * Fetch agents statistics from the API
 * @param options Optional parameters including date range, pagination, and search
 * @returns Promise with agents stats data
 */
export const fetchAgentsStats = async (
  options?: {
    startDate?: string;
    endDate?: string;
    skip?: number;
    limit?: number;
    search?: string;
  }
): Promise<AgentsStatsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-stats/agents`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    if (options?.skip !== undefined) {
      params.append('skip', options.skip.toString());
    }

    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }

    if (options?.search) {
      params.append('search', options.search);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch agents stats');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching agents stats:', error);
    throw error;
  }
};

/**
 * Fetch company statistics from the API
 * @param options Optional parameters including date range
 * @returns Promise with company stats data
 */
export const fetchCompanyStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<CompanyStatsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-stats/company`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch company stats');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching company stats:', error);
    throw error;
  }
};

/**
 * Fetch combined totals statistics from the API
 * @param options Optional parameters including date range
 * @returns Promise with totals stats data
 */
export const fetchTotalsStats = async (
  options?: { startDate?: string; endDate?: string }
): Promise<TotalsStatsResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-stats/totals`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch totals stats');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching totals stats:', error);
    throw error;
  }
};

/**
 * Fetch ads performance details for a specific user (company or agent)
 * @param userId User ID (company or agent)
 * @param options Optional parameters including pagination
 * @returns Promise with ads performance data
 */
export const fetchAdsPerformance = async (
  userId: string,
  options?: {
    limit?: number;
    skip?: number;
  }
): Promise<AdsPerformanceResponse> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-stats/ads-performance/${userId}`;
    const params = new URLSearchParams();

    if (options?.limit !== undefined) {
      params.append('$limit', options.limit.toString());
    }

    if (options?.skip !== undefined) {
      params.append('$skip', options.skip.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch ads performance');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching ads performance:', error);
    throw error;
  }
};

/**
 * Fetch advertisers performance list (company + agents)
 * @param options Optional parameters including date range and pagination
 * @returns Promise with advertisers performance data
 */
export const fetchAdvertisersPerformance = async (
  options?: {
    startDate?: string;
    endDate?: string;
    skip?: number;
    limit?: number;
  }
): Promise<any> => {
  try {
    const { accessToken } = store.getState().auth;

    let url = `${config.BASE_URL}/company-stats/advertisers-performance`;
    const params = new URLSearchParams();

    if (options?.startDate) {
      params.append('startDate', options.startDate);
    }

    if (options?.endDate) {
      params.append('endDate', options.endDate);
    }

    if (options?.skip !== undefined) {
      params.append('skip', options.skip.toString());
    }

    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    const result = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!result.ok) {
      throw new Error('Failed to fetch advertisers performance');
    }

    return await result.json();
  } catch (error) {
    console.error('Error fetching advertisers performance:', error);
    throw error;
  }
};

